/**
 * Whether a WPS pipe-set section JSON contains user-meaningful saved data.
 * Used for edit-lock UX: only lock + show Edit when these return true.
 */

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function nonEmptyString(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0
}

function nonEmptyStringArray(v: unknown): boolean {
  return Array.isArray(v) && v.some((x) => typeof x === 'string' && x.trim().length > 0)
}

/** Base material: lots or legacy flat fields */
export function hasBaseMaterialData(section: unknown): boolean {
  if (!isRecord(section)) return false
  const lots = section.manufacturerLots
  if (Array.isArray(lots) && lots.length > 0) return true
  return Boolean(
    nonEmptyStringArray(section.materialSpec) ||
      nonEmptyStringArray(section.typeGrade) ||
      nonEmptyStringArray(section.slabSource) ||
      nonEmptyStringArray(section.plateSource) ||
      nonEmptyStringArray(section.pipeManufacturer) ||
      nonEmptyStringArray(section.heatNumbers)
  )
}

function bevelEntryOccupied(b: Record<string, unknown>): boolean {
  return (
    nonEmptyStringArray(b.jointType) ||
    nonEmptyStringArray(b.backingType) ||
    nonEmptyStringArray(b.backingMaterial) ||
    nonEmptyString(b.pipe1Id) ||
    nonEmptyString(b.pipe2Id)
  )
}

export function hasBevelData(section: unknown): boolean {
  if (!isRecord(section)) return false
  const bevels = section.bevels
  if (Array.isArray(bevels) && bevels.length > 0) {
    if (bevels.some((b) => isRecord(b) && bevelEntryOccupied(b))) return true
    if (bevels.length > 1) return true
  }
  return Boolean(
    nonEmptyStringArray(section.jointType) ||
      nonEmptyStringArray(section.backingType) ||
      nonEmptyStringArray(section.backingMaterial)
  )
}

export function hasPreheatData(section: unknown): boolean {
  if (!isRecord(section)) return false
  if (nonEmptyString(section.preheatMin) || nonEmptyString(section.preheatMax)) return true
  if (nonEmptyString(section.interpassMin) || nonEmptyString(section.interpassMax)) return true
  if (nonEmptyString(section.preheatNote) || nonEmptyString(section.interpassNote)) return true
  if (nonEmptyStringArray(section.heatingMethod) || nonEmptyStringArray(section.controlMethod)) {
    return true
  }
  const pu = section.preheatUnit
  const iu = section.interpassUnit
  if (typeof pu === 'string' && pu === '°C') return true
  if (typeof iu === 'string' && iu === '°C') return true
  return false
}

function passPowerValuesNonEmpty(v: unknown): boolean {
  if (!isRecord(v)) return false
  return Object.values(v).some((x) => typeof x === 'string' && x.trim().length > 0)
}

function powerDetailsMeaningful(v: unknown): boolean {
  if (!isRecord(v)) return false
  return Object.values(v).some((detail) => {
    if (!isRecord(detail)) return false
    const fw = detail.firmwareVersions
    const pr = detail.programs
    if (nonEmptyStringArray(fw) || nonEmptyStringArray(pr)) return true
    if (detail.enabled === false) return true
    return false
  })
}

export function hasClampData(section: unknown): boolean {
  if (!isRecord(section)) return false
  if (nonEmptyStringArray(section.clampType)) return true
  if (nonEmptyString(section.removalNote)) return true
  if (passPowerValuesNonEmpty(section.passPowerSupplies)) return true
  if (nonEmptyStringArray(section.powerSupplyOptions)) return true
  if (powerDetailsMeaningful(section.powerSupplyDetails)) return true
  if (nonEmptyStringArray(section.firmwareVersions) || nonEmptyStringArray(section.programs)) {
    return true
  }
  const cc = section.clampCount
  if (typeof cc === 'string' && cc.trim() && cc !== '2') return true
  if (typeof cc === 'number' && cc !== 2) return true
  return false
}

export function hasPostWeldData(postWeld: unknown, cleaningGrinding: unknown): boolean {
  if (isRecord(postWeld)) {
    if (
      nonEmptyStringArray(postWeld.postHeating) ||
      nonEmptyStringArray(postWeld.pwht) ||
      nonEmptyStringArray(postWeld.coolingMethod)
    ) {
      return true
    }
    if (
      nonEmptyString(postWeld.notes) ||
      nonEmptyString(postWeld.cleaningNotes) ||
      nonEmptyString(postWeld.rootExternalTreatment)
    ) {
      return true
    }
  }
  if (isRecord(cleaningGrinding) && nonEmptyString(cleaningGrinding.cleaningNotes)) return true
  return false
}

/** Auto-generated report tags stored in `sections.reports.items` for history/download only. */
const AUTO_GENERATED_REPORT_TAGS = new Set(['PQR', 'WPS'])

/**
 * Predicate: does this raw `sections.reports.items[i]` entry represent a
 * user-uploaded supporting document that belongs in the Accompanying
 * Reports surface (the WpsReports page list AND the WpsForm card count)?
 *
 * User-uploaded supporting documents only — never auto-generated WPS or
 * PQR PDFs. Excludes:
 *   - malformed entries (non-object, missing `id` or `fileKey`)
 *   - soft-deleted entries (`deletedAt` is a non-empty string)
 *   - auto-generated PQR PDFs (`tag === 'PQR'`) — see `PqrReportHistory`
 *   - auto-generated WPS PDFs (`tag === 'WPS'`) — see `WpsReportHistory`
 *
 * Mirrors the server-side filter in `mapAccompanyingReports`
 * (server/src/pdf/pqrDataMapper.ts).
 *
 * Use this single helper everywhere we render or count Accompanying
 * Reports so the UI surfaces stay in sync.
 */
export function isUserAccompanyingReport(raw: unknown): boolean {
  if (!isRecord(raw)) return false
  if (typeof raw.deletedAt === 'string' && raw.deletedAt.trim().length > 0) return false
  if (typeof raw.tag === 'string' && AUTO_GENERATED_REPORT_TAGS.has(raw.tag)) return false
  const id = raw.id
  const fileKey = raw.fileKey
  return (
    typeof id === 'string' &&
    id.length > 0 &&
    typeof fileKey === 'string' &&
    fileKey.length > 0
  )
}

function reportItemsMeaningful(items: unknown): boolean {
  if (!Array.isArray(items)) return false
  return items.some(isUserAccompanyingReport)
}

export function hasReportsData(section: unknown): boolean {
  if (!isRecord(section)) return false
  return reportItemsMeaningful(section.items)
}

export function hasMetaCommentsData(meta: unknown, comments: unknown): boolean {
  if (isRecord(meta) && nonEmptyString(meta.wpsNumber)) return true
  if (typeof comments === 'string' && comments.trim().length > 0) return true
  return false
}

function weldHasUserData(w: Record<string, unknown>): boolean {
  if (nonEmptyString(w.weldNumber) || nonEmptyString(w.weldId) || nonEmptyString(w.sheetName)) return true
  if (nonEmptyString(w.pupOne) || nonEmptyString(w.pupTwo) || nonEmptyString(w.pupNumber)) return true
  if (nonEmptyStringArray(w.heatNumbers)) return true
  const ops = w.operators
  if (isRecord(ops)) {
    if (nonEmptyStringArray(ops.equipment)) return true
    if (nonEmptyString(ops.notes)) return true
    const om = ops.operatorMap
    if (isRecord(om)) {
      const anyOp = Object.values(om).some(
        (e) =>
          isRecord(e) && (nonEmptyString(e.name) || nonEmptyString(e.id))
      )
      if (anyOp) return true
    }
  }
  const pr = w.passRecords
  if (
    Array.isArray(pr) &&
    pr.some(
      (r) =>
        isRecord(r) &&
        (nonEmptyString((r as Record<string, unknown>).weldId) ||
          nonEmptyString((r as Record<string, unknown>).weldNumber))
    )
  ) {
    return true
  }
  if (Array.isArray(pr) && pr.length > 0) return true
  const bmp = w.bevelMeasurementsByPup
  if (isRecord(bmp)) {
    const checkMatrix = (m: unknown) => {
      if (!isRecord(m)) return false
      return Object.values(m).some((row) => {
        if (!isRecord(row)) return false
        return Object.values(row).some((cell) => nonEmptyString(cell))
      })
    }
    if (checkMatrix(bmp.pupOne) || checkMatrix(bmp.pupTwo)) return true
  }
  return false
}

export function hasFloorsheetData(section: unknown): boolean {
  if (!isRecord(section)) return false
  const welds = section.welds
  if (Array.isArray(welds) && welds.length > 0) {
    if (welds.some((w) => isRecord(w) && weldHasUserData(w))) return true
    if (welds.length > 1) return true
  }
  if (nonEmptyString(section.weldNumber) || nonEmptyString(section.sheetName)) return true
  if (isRecord(section.operators) && nonEmptyStringArray(section.operators.equipment)) return true
  const pr = section.passRecords
  return Array.isArray(pr) && pr.length > 0
}
